import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function RegisterPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', mobile: '', password: '', confirm: '' });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error('Passwords do not match'); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const res = await authApi.customerRegister({
        name: form.name, email: form.email,
        mobile: form.mobile, password: form.password
      });
      if (!res.success) throw new Error(res.message);
      login(res.data, 'customer');
      toast.success('Account created! Welcome to ECL.');
      navigate('/customer');
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // -- Tokens --
  const NAVY       = "var(--navy, #0A1838)";
  const AMBER      = "var(--amber, #F59E0B)";
  const CREAM      = "var(--cream, #FAF7F2)";
  const CREAM_WARM = "var(--cream-warm, #F1ECDF)";
  const PAPER      = "var(--paper, #FFFFFF)";
  const INK        = "var(--ink, #1F2937)";
  const MUTED      = "var(--muted, #6B7280)";
  const LINE       = "var(--line, #E5E7EB)";
  const SERIF      = 'var(--font-display, "DM Serif Display", Georgia, serif)';
  const SANS       = 'var(--font-body, "Inter Tight", -apple-system, sans-serif)';

  const S = {
    page:      { minHeight:"100vh", display:"grid", gridTemplateColumns:"1fr 1.1fr",
                 fontFamily:SANS, background:CREAM },
    left:      { display:"flex", alignItems:"center", justifyContent:"center",
                 padding:"40px 32px", background:CREAM, overflowY:"auto" },
    wrap:      { width:"100%", maxWidth:480 },
    logo:      { display:"flex", alignItems:"center", gap:10, marginBottom:24 },
    logoMark:  { fontSize:"1.4rem", color:AMBER, lineHeight:1 },
    logoName:  { fontFamily:SANS, fontSize:"1.05rem", fontWeight:700,
                 color:NAVY, letterSpacing:"-0.01em" },
    title:     { fontFamily:SERIF, fontSize:"1.9rem", fontWeight:400,
                 color:NAVY, marginBottom:6, letterSpacing:"-0.02em" },
    subtitle:  { fontSize:"0.95rem", color:MUTED, marginBottom:28 },
    label:     { display:"block", fontSize:"0.72rem", fontWeight:600,
                 color:MUTED, textTransform:"uppercase",
                 letterSpacing:"0.06em", marginBottom:6 },
    req:       { color:"#EF4444", marginLeft:2 },
    input:     { width:"100%", padding:"12px 14px",
                 background:PAPER, border:`1px solid ${LINE}`,
                 borderRadius:10, color:INK,
                 fontSize:"0.95rem", fontFamily:SANS,
                 outline:"none", minHeight:48 },
    row:       { display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 },
    field:     { display:"flex", flexDirection:"column", gap:6, marginBottom:14 },
    submit:    { width:"100%", padding:"14px 24px",
                 background:NAVY, color:PAPER, border:"none",
                 borderRadius:10, fontSize:"0.98rem", fontWeight:500,
                 cursor:"pointer", fontFamily:SANS, minHeight:50,
                 boxShadow:"0 4px 14px rgba(10,24,56,0.25)",
                 marginTop:8 },
    signInLink:{ textAlign:"center", marginTop:20, fontSize:"0.88rem", color:MUTED },
    right:     { padding:"60px 56px", display:"flex", flexDirection:"column",
                 justifyContent:"center", position:"relative", overflow:"hidden",
                 background:`linear-gradient(180deg, ${CREAM_WARM} 0%, ${CREAM} 100%)`,
                 borderLeft:`1px solid ${LINE}` },
    orb:       { position:"absolute", top:"-200px", left:"-200px",
                 width:600, height:600,
                 background:"radial-gradient(circle, rgba(245,158,11,0.08), transparent 70%)",
                 pointerEvents:"none" },
    rightInner:{ position:"relative", zIndex:2, maxWidth:520 },
    eyebrow:   { display:"inline-block", fontSize:"0.72rem",
                 letterSpacing:"0.15em", fontWeight:600,
                 color:AMBER, marginBottom:18, textTransform:"uppercase" },
    h1:        { fontFamily:SERIF, fontWeight:400,
                 fontSize:"clamp(2rem, 4vw, 3.2rem)",
                 lineHeight:1.05, letterSpacing:"-0.02em",
                 color:NAVY, marginBottom:24 },
    italic:    { fontStyle:"italic", color:AMBER },
    sub:       { fontSize:"1rem", color:MUTED,
                 lineHeight:1.6, marginBottom:32, maxWidth:440 },
    benefitList: { display:"flex", flexDirection:"column", gap:14 },
    benefit:   { display:"flex", gap:14, alignItems:"flex-start" },
    benefitNum:{ fontFamily:SERIF, fontSize:"1.1rem",
                 color:AMBER, lineHeight:1, flexShrink:0,
                 letterSpacing:"-0.02em" },
    benefitText:{ fontSize:"0.92rem", color:INK, lineHeight:1.5 },
  };

  return (
    <div style={S.page}>
      {/* -- LEFT: Form -- */}
      <div style={S.left}>
        <div style={S.wrap}>
          <div style={S.logo}>
            <span style={S.logoMark}>{'\u26A1'}</span>
            <span style={S.logoName}>CargoPulse</span>
          </div>

          <h2 style={S.title}>Create your account</h2>
          <p style={S.subtitle}>Start shipping in minutes &mdash; free, no commitment.</p>

          <form onSubmit={handleSubmit}>
            <div style={S.field}>
              <label style={S.label}>Full Name<span style={S.req}>*</span></label>
              <input style={S.input} placeholder="Muhammad Ali" value={form.name} onChange={set('name')} required />
            </div>
            <div style={S.row}>
              <div style={S.field}>
                <label style={S.label}>Email<span style={S.req}>*</span></label>
                <input style={S.input} type="email" placeholder="you@example.com" value={form.email} onChange={set('email')} required />
              </div>
              <div style={S.field}>
                <label style={S.label}>Mobile<span style={S.req}>*</span></label>
                <input style={S.input} placeholder="+923001234567" value={form.mobile} onChange={set('mobile')} required />
              </div>
            </div>
            <div style={S.row}>
              <div style={S.field}>
                <label style={S.label}>Password<span style={S.req}>*</span></label>
                <input style={S.input} type="password" placeholder="Min 8 chars" value={form.password} onChange={set('password')} required />
              </div>
              <div style={S.field}>
                <label style={S.label}>Confirm<span style={S.req}>*</span></label>
                <input style={S.input} type="password" placeholder="Repeat password" value={form.confirm} onChange={set('confirm')} required />
              </div>
            </div>

            <button style={{...S.submit, opacity: loading ? 0.7 : 1}} type="submit" disabled={loading}>
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <p style={S.signInLink}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: NAVY, fontWeight: 600, textDecoration:'underline', textUnderlineOffset:3 }}>
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* -- RIGHT: Hero benefits -- */}
      <div style={S.right}>
        <div style={S.orb} />
        <div style={S.rightInner}>
          <span style={S.eyebrow}>WELCOME TO CARGOPULSE</span>
          <h1 style={S.h1}>
            Ship smarter,<br/>
            <em style={S.italic}>track everything.</em>
          </h1>
          <p style={S.sub}>
            Join thousands of senders and businesses moving cargo across Pakistan with real-time tracking, transparent pricing, and dedicated COD remittance.
          </p>

          <div style={S.benefitList}>
            {[
              ['01', 'Same-day, Express, Standard, and Economy delivery options'],
              ['02', 'Real-time tracking with SMS, email, and WhatsApp notifications'],
              ['03', 'Cash on Delivery, OTP-secured, T+1 remitted to you'],
              ['04', 'Transparent rate calculator before every booking'],
              ['05', 'Corporate accounts with credit terms and API integrations'],
            ].map(([n, t]) => (
              <div key={n} style={S.benefit}>
                <div style={S.benefitNum}>{n}</div>
                <div style={S.benefitText}>{t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
